import type { Delivery, DeliveryStatus } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { listDeliveries } from '../../lib/delivery.js';
import { formatMoney } from '../../lib/money.js';

const FILTERS: Array<DeliveryStatus | 'all'> = ['all', 'pending', 'assigned', 'out_for_delivery', 'delivered', 'failed'];
const TONE: Record<DeliveryStatus, 'secondary' | 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'secondary',
  assigned: 'info',
  out_for_delivery: 'warning',
  delivered: 'success',
  failed: 'danger',
};

export function DeliveryBoardPage(): JSX.Element {
  const { t, i18n } = useTranslation('delivery');
  const [filter, setFilter] = useState<DeliveryStatus | 'all'>('all');
  const [rows, setRows] = useState<Delivery[] | null>(null);

  useEffect(() => {
    setRows(null);
    listDeliveries(filter === 'all' ? {} : { status: filter })
      .then(setRows)
      .catch(() => setRows([]));
  }, [filter]);

  const money = (v: string | null, c: string | null): string => (v && c ? formatMoney(v, { locale: i18n.language, currency: c }) : '—');

  const columns: DataTableColumn<Delivery>[] = [
    {
      id: 'docNumber', header: t('board.number'), sortValue: (r) => r.docNumber, searchValue: (r) => `${r.docNumber} ${r.sourceNumber} ${r.recipientName ?? ''}`,
      cell: (r) => <Link to={`/delivery/${r.id}`} className="font-mono font-medium text-brand hover:underline">{r.docNumber}</Link>,
    },
    { id: 'order', header: t('board.order'), sortValue: (r) => r.sourceNumber, cell: (r) => <span className="font-mono text-fg-muted">{r.sourceNumber || '—'}</span> },
    { id: 'recipient', header: t('board.recipient'), sortValue: (r) => r.recipientName ?? '', cell: (r) => r.recipientName ?? '—' },
    { id: 'city', header: t('board.city'), sortValue: (r) => r.city ?? '', cell: (r) => r.city ?? '—' },
    {
      id: 'driver', header: t('board.driver'), sortValue: (r) => r.driverName ?? '',
      cell: (r) => (r.driverName ? r.driverName : <span className="text-fg-subtle">{t('board.unassigned')}</span>),
    },
    {
      id: 'status', header: t('board.status', { defaultValue: 'Status' }), sortValue: (r) => r.status,
      cell: (r) => <Badge variant={TONE[r.status]}>{t(`status.${r.status}`)}</Badge>,
    },
    { id: 'total', header: t('board.total'), align: 'end', cellClassName: 'font-mono tabular-nums', sortValue: (r) => Number(r.orderTotal ?? 0), cell: (r) => money(r.orderTotal, r.currencyCode) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('board.heading')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('board.subtitle')}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs ${filter === f ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg'}`}
          >
            {f === 'all' ? t('board.filterAll') : t(`status.${f}`)}
          </button>
        ))}
      </div>

      {!rows ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : rows.length === 0 ? (
        <EmptyState title={t('board.empty')} description={t('board.emptyHint')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={rows} rowKey={(r) => r.id} searchPlaceholder={t('board.search')} />
        </Card>
      )}
    </div>
  );
}
